import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form, Button, Select, Card, notification } from 'antd';

import {
  DynamicFormField,
  DynamicFormFieldItem,
  PercentInput,
} from 'components';
import {
  selectors,
  updatePlayerBenchmarkComposition,
  BenchmarkComposition as BenchmarkCompositionType,
} from 'state';

const hasValidCompositionWeights = (
  composition: BenchmarkCompositionType[] = []
) => {
  if (!composition.length) {
    notification.error({
      message: 'Benchmark composition cannot be empty.',
    });
    return false;
  }

  const hasInvalidWeight = composition.some(
    item => item.weight == null || item.weight < 1 || item.weight > 100
  );
  if (hasInvalidWeight) {
    notification.error({
      message: 'Each benchmark weight must be between 1% and 100%.',
    });
    return false;
  }

  const totalWeight = composition.reduce((sum, item) => sum + item.weight, 0);
  if (Math.abs(totalWeight - 100) > 0.01) {
    notification.error({
      message: `Benchmark weights must add up to 100%. Current total: ${totalWeight.toFixed(
        2
      )}%.`,
    });
    return false;
  }

  return true;
};

const BenchmarkComposition: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const indexFunds = useSelector(selectors.indexFundsList);
  const assets = useSelector(selectors.underlyingAssetsList);

  const session = useSelector(selectors.activeSession);
  const player = useSelector(selectors.player);

  let benchmark_compositions;
  if (session.allow_individual_benchmark && player.benchmark_compositions) {
    benchmark_compositions = player.benchmark_compositions;
  } else {
    benchmark_compositions = session.benchmark_compositions;
  }
  const benchmark_data = benchmark_compositions.map(item => ({
    ...item,
    fund_or_asset_ric: item.ric,
    weight: item.weight * 100,
  }));

  const equities = assets
    .filter(item => item.resourcetype === 'Equity')
    .map(item => ({
      id: item.id,
      ric: item.ric,
      name: item.name,
      issuer_name: '',
      decommissioned: false,
    }));

  // Decommissioned funds are dropped from the pick list (can't be newly selected). One already held
  // in this player's basket still renders as its selected value (antd shows the stored ric).
  const compositeList = useMemo(
    () => [...indexFunds, ...equities].filter(item => !item.decommissioned),
    [indexFunds, equities]
  );

  // console.log(compositeList);

  const onFinish = () => {
    const rawComposition = form.getFieldValue('composition') || [];
    if (!hasValidCompositionWeights(rawComposition)) {
      return;
    }

    const composition = rawComposition.map(
      (item: BenchmarkCompositionType) => ({
        ...item,
        weight: item.weight / 100,
      })
    );

    dispatch(
      updatePlayerBenchmarkComposition({ composition, player_id: player.id })
    );
  };

  return (
    <Card title="Benchmark Composition">
      <Form
        form={form}
        onFinish={onFinish}
        initialValues={{ composition: benchmark_data }}
      >
        <DynamicFormField
          name="composition"
          addNewText="Add Fund"
          renderField={field => (
            <>
              <DynamicFormFieldItem
                field={field}
                label="Fund"
                name="fund_or_asset_ric"
                missingMessage="Missing Fund"
              >
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  style={{ width: 200 }}
                >
                  {compositeList.map(item => (
                    <Select.Option key={item.ric} value={item.ric}>
                      {item.ric}
                    </Select.Option>
                  ))}
                </Select>
              </DynamicFormFieldItem>

              <DynamicFormFieldItem
                field={field}
                label="Weight"
                name="weight"
                missingMessage="Missing Weight"
              >
                <PercentInput min={1} max={100} />
              </DynamicFormFieldItem>
            </>
          )}
        />

        <Form.Item>
          <Button type="primary" htmlType="submit">
            Save
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default BenchmarkComposition;
